import React, { useState } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import Space from "antd/lib/space";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import styled from "styled-components";
import { useParams } from "react-router-dom";

import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import { CommonPatientExtensionItems } from "pages/patients/commonPatientExtensionItems";

import { PatientDetail } from "./patientDetail";
import { TasksTable } from "./tasksTable";

export interface Task {
  id: string;
  taskName: string;
  description: string;
  implementorRole: string;
  assignedTo: string;
  status: "Not Started" | "In Progress" | "Completed";

  subTasks?: string[];
  consumables?: ConsumableItem[];
  keyActions?: string;

  orderedItems?: { [key: string]: string | number };
}

interface ConsumableItem {
  id: number; // unique ID
  name: string; // display name
}

export interface OrderGroup {
  orderName: string;
  templateId: string;
  tasks: Task[];
}

export const TasksPage: React.FC = () => {
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const { id } = useParams<{ id: string }>();

  const userRole =
    sessionStorage.getItem("userRole") === "physician"
      ? "Doctor"
      : sessionStorage.getItem("userRole") || "caregiver";

  const [orders, setOrders] = useState<OrderGroup[]>(
    id === "3083"
      ? [
          {
            orderName: "NG Tube Procedure",
            templateId: "NGT-001",
            tasks: [
              {
                id: "T-001",
                taskName: "Clinical Suitability Review",
                description:
                  "Evaluate patient condition to confirm NG tube insertion is clinically appropriate.",
                implementorRole: "Doctor",
                assignedTo: "ajit gite",
                status: "Not Started",
                subTasks: [
                  "Assess diagnosis",
                  "Check vitals (BP, HR, oxygen)",
                  "Confirm NG tube indication",
                  "Rule out contraindications"
                ],
                consumables: [],
                keyActions: "Only mark sub task as completed",
                orderedItems: {}
              },
              {
                id: "T-002",
                taskName: "Consent Documentation",
                description:
                  "Obtain patient or family consent and document consent form.",
                implementorRole: "Nurse",
                assignedTo: "rachin ravi",
                status: "Not Started",
                subTasks: [
                  "Explain procedure to patient/family",
                  "Highlight risks & benefits",
                  "Obtain written consent",
                  "Record in system"
                ],
                consumables: [],
                keyActions: "Send or take Consent from Patient",
                orderedItems: {}
              },
              {
                id: "T-003",
                taskName: "NG Tube Preparation",
                description:
                  "Prepare equipment including NG tube, lubricant gel, syringe and gloves.",
                implementorRole: "Nurse",
                assignedTo: "rachin ravi",
                status: "Not Started",
                subTasks: [
                  "Gather required items",
                  "Check tube size suitability",
                  "Maintain sterile setup",
                  "Prepare tray"
                ],
                consumables: [
                  { id: 42, name: "NG Tube" },
                  { id: 43, name: "Gloves" },
                  { id: 44, name: "Lubricant Gel" },
                  { id: 45, name: "Syringe" },
                  { id: 46, name: "Tape" },
                  { id: 47, name: "Kidney Tray" }
                ],
                keyActions: "Use / order consumables",
                orderedItems: {}
              },
              {
                id: "T-004",
                taskName: "NG Tube Insertion",
                description:
                  "Perform insertion according to clinical protocol.",
                implementorRole: "Doctor",
                assignedTo: "ajit gite",
                status: "Not Started",
                subTasks: [
                  "Position patient (semi-upright)",
                  "Wear sterile gloves",
                  "Lubricate tube",
                  "Insert via nostril",
                  "Confirm placement (aspiration / X-ray)",
                  "Secure tube with tape"
                ],
                consumables: [
                  { id: 42, name: "NG Tube" },
                  { id: 43, name: "Gloves" },
                  { id: 44, name: "Lubricant Gel" },
                  { id: 45, name: "Syringe" },
                  { id: 48, name: "pH strips" },
                  { id: 46, name: "Tape" }
                ],
                keyActions: "Use / order consumables",
                orderedItems: {}
              },
              {
                id: "T-005",
                taskName: "Post-Insertion Monitoring",
                description:
                  "Monitor patient for complications after insertion such as discomfort, breathing difficulty, or tube displacement.",
                implementorRole: "Nurse",
                assignedTo: "rachin ravi",
                status: "Not Started",
                subTasks: [
                  "Check breathing & comfort",
                  "Monitor for coughing/vomiting",
                  "Verify tube position periodically",
                  "Record vitals",
                  "Escalate complications"
                ],
                consumables: [{ id: 43, name: "Gloves" }],
                keyActions: "Only mark sub task as completed",
                orderedItems: {}
              },
              {
                id: "T-006",
                taskName: "Feeding Protocol Setup",
                description:
                  "Configure feeding schedule and nutritional formula according to patient dietary plan.",
                implementorRole: "Nurse",
                assignedTo: "rachin ravi",
                status: "Not Started",
                subTasks: [
                  "Assess nutritional needs (calories, protein)",
                  "Decide feed type (liquid formula)",
                  "Define feeding method (bolus/continuous)",
                  "Set feeding frequency & volume",
                  "Document diet plan",
                  "Prepare feed (nurse)",
                  "Administer via tube",
                  "Flush tube before/after feed"
                ],
                consumables: [
                  { id: 50, name: "Feeding formula" },
                  { id: 51, name: "Syringe/Feeding pump" },
                  { id: 52, name: "Water" },
                  { id: 43, name: "Gloves" }
                ],
                keyActions: "Only mark sub task as completed",
                orderedItems: {}
              }
            ]
          },
          {
            orderName: "External Chest X-Ray",
            templateId: "XR-EXT-002",
            tasks: [
              {
                id: "T-101",
                taskName: "Transport Booking (Outbound)",
                description:
                  "Book and coordinate ambulance for patient transfer to Radiology Center.",
                implementorRole: "Nurse",
                assignedTo: "rachin ravi",
                status: "Not Started",
                subTasks: [
                  "Call ambulance provider",
                  "Confirm pickup time (14:00)",
                  "Prepare patient transfer form",
                  "Brief ambulance crew on patient condition"
                ],
                consumables: [],
                keyActions: "Confirm booking with vendor",
                orderedItems: { service: "Ambulance - Basic Life Support" }
              },
              {
                id: "T-102",
                taskName: "Patient Escort & Preparation",
                description:
                  "Prepare patient for travel and ensure clinical stability.",
                implementorRole: "Caregiver",
                assignedTo: "Vibhu Bansal",
                status: "Not Started",
                subTasks: [
                  "Check baseline vitals",
                  "Ensure patient is in wheelchair/stretcher",
                  "Secure oxygen tank (if required)",
                  "Handover medical records to escort"
                ],
                consumables: [
                  { id: 60, name: "Portable Oxygen Tank" },
                  { id: 43, name: "Gloves" }
                ],
                keyActions: "Only mark sub task as completed",
                orderedItems: {}
              },
              {
                id: "T-103",
                taskName: "External X-Ray Procedure",
                description:
                  "Perform Chest X-Ray at Diagnostic Imaging Center.",
                implementorRole: "Caregiver",
                assignedTo: "Vibhu Bansal",
                status: "Not Started",
                subTasks: [
                  "Verify patient identity (2 identifiers)",
                  "Position patient for PA/Lateral view",
                  "Capture X-ray image",
                  "Review image for quality"
                ],
                consumables: [],
                keyActions: "Upload digital image to portal",
                orderedItems: { procedure: "Chest X-Ray (Single View)" }
              },
              {
                id: "T-104",
                taskName: "Return Transport Coordination",
                description:
                  "Coordinate return trip back to LTC facility after procedure.",
                implementorRole: "Caregiver",
                assignedTo: "Vibhu Bansal",
                status: "Not Started",
                subTasks: [
                  "Notify ambulance for return pickup",
                  "Check patient vitals post-procedure",
                  "Collect physical radiology report (if any)",
                  "Ensure safe transfer back to ward bed"
                ],
                consumables: [],
                keyActions: "Confirm return arrival",
                orderedItems: {}
              },
              {
                id: "T-105",
                taskName: "Clinical Review of Results",
                description:
                  "Doctor to review X-ray results for NG tube confirmation or pathology.",
                implementorRole: "Doctor",
                assignedTo: "ajit gite",
                status: "Not Started",
                subTasks: [
                  "Access digital imaging system",
                  "Verify NG tube tip location",
                  "Check for pulmonary complications",
                  "Document findings in progress notes"
                ],
                consumables: [],
                keyActions: "Finalize order status",
                orderedItems: {}
              }
            ]
          }
        ]
      : []
  );

  // ✅ UPDATE TASK STATUS
  const updateTaskStatus = (taskId: string, status: Task["status"]) => {
    setOrders(prev =>
      prev.map(order => ({
        ...order,
        tasks: order.tasks.map(t => (t.id === taskId ? { ...t, status } : t))
      }))
    );

    setSelectedTask(prev =>
      prev && prev.id === taskId ? { ...prev, status } : prev
    );
  };

  // ✅ UPDATE ORDERED ITEMS (PERSISTENT)
  const updateOrderedItems = (
    taskId: string,
    item: number,
    quantity: number
  ) => {
    setOrders(prev =>
      prev.map(order => ({
        ...order,
        tasks: order.tasks.map(t =>
          t.id === taskId
            ? {
                ...t,
                orderedItems: {
                  ...(t.orderedItems || {}),
                  [item]: quantity
                }
              }
            : t
        )
      }))
    );

    setSelectedTask(prev =>
      prev && prev.id === taskId
        ? {
            ...prev,
            orderedItems: {
              ...(prev.orderedItems || {}),
              [item]: quantity
            }
          }
        : prev
    );
  };

  return (
    <>
      <CommonPatientExtensionItems />

      <Row>
        <Col>
          <TitleCol>{selectedTask ? "Task Details" : "Order Tasks"}</TitleCol>

          {selectedTask && (
            <Space style={{ padding: "0 0 20px 20px" }}>
              <AppButton
                type={ButtonType.Link}
                buttonContent={
                  <Space>
                    <ArrowLeftOutlined />
                    Back to Tasks
                  </Space>
                }
                onClick={() => setSelectedTask(null)}
              />
            </Space>
          )}
        </Col>
      </Row>

      {orders.length === 0 ? (
        <NoData>No Tasks Available</NoData>
      ) : selectedTask ? (
        <PatientDetail
          task={selectedTask}
          updateStatus={updateTaskStatus}
          updateOrderedItems={updateOrderedItems}
          userRole={userRole}
        />
      ) : (
        <TasksTable
          orders={orders}
          setSelectedTask={setSelectedTask}
          userRole={userRole}
        />
      )}
    </>
  );
};

const TitleCol = styled(Col)`
  padding: 20px;
  font-size: 18px;
  font-weight: bold;
`;

const NoData = styled.div`
  padding: 40px;
  text-align: center;
  color: #9e9e9e;
  font-size: 16px;
`;
